// [COMPOSANT] Page dédiée STMicroelectronics avec SSO uniquement
// [RÔLE] Interface sécurisée pour domaine ST avec accès restreint
// [ARCHITECTURE] Design Famla identique + bouton SSO ST uniquement

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { removeAuthCookie, setAuthCookie } from '../../utils/authUtils';
import { AuthLayout } from '../../layouts';
import { Buttons } from '../../components';
import { config } from '../../services/config';
import { Routes } from '../../routes';
import { useAppDispatch } from '../../stores';
import { login } from '../../stores';
import { iSessions } from '../../features/auth';
import { FamlaServices } from '../../services';
import { validateInvitationToken, InvitationTokenResult, InvitationTokenData } from '../../utils/invitationToken';
import { getTenantName } from '../../utils/brandingStyles';

const STDedicatedView: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [invitationError, setInvitationError] = useState<string | null>(null);
    const [oauthError, setOauthError] = useState<string | null>(null);
    const invitationProcessedRef = useRef(false);
    const sessionLoadedRef = useRef(false);

    const loadSession = useCallback(async (token: string) => {
        // [VÉRIFICATION DOUBLE APPEL] Éviter de charger la session plusieurs fois
        if (sessionLoadedRef.current) {
            console.log('🔄 [ST Portal] Session already loaded, skipping...');
            return;
        }
        
        try {
            sessionLoadedRef.current = true;
            setIsAuthenticating(true);
            console.log('🔄 [ST Portal] Loading session with token...');
            let data = await FamlaServices("api_host", `/auth/me`, "GET", "", token);
            if (data.status === 200) {
                const session: iSessions = data.body.data;
                console.log('✅ [ST Portal] Session loaded:', {
                    company_id: session.company_id,
                    user_verified: session.user?.verified
                });
                
                // [VÉRIFICATION EMAIL]
                if (session.user && !session.user.verified) {
                    console.log('❌ [ST Portal] User not verified, clearing tokens');
                    removeAuthCookie('access_token');
                    removeAuthCookie('refresh_token');
                    removeAuthCookie('logged_in');
                    setIsAuthenticating(false);
                    return;
                }

                dispatch(login({ token: token, session: session, user: session.user }));

                // [ANNONCES] Stocker les annonces en attente (même logique que le login classique)
                const pendingAnnouncements: string[] = (session.user as any)?.pending_announcements ?? [];
                sessionStorage.setItem('pending_announcements', JSON.stringify(pendingAnnouncements));
                
                // [VÉRIFICATION INVITATION DÉJÀ TRAITÉE] Éviter de traiter l'invitation plusieurs fois
                if (invitationProcessedRef.current) {
                    console.log('🔗 [ST Portal] Invitation already processed, skipping');
                    setIsAuthenticating(false);
                    return;
                }
                
                // [INVITATION] Vérifier si l'utilisateur a une invitation en attente
                const invitationToken = sessionStorage.getItem('invitation_token');
                const invitedProcessId = sessionStorage.getItem('invited_process_id');
                const invitedUserEmail = sessionStorage.getItem('invited_user_email');
                
                if (invitationToken && invitedProcessId) {
                    // [VÉRIFICATION EMAIL] Vérifier que l'email correspond
                    if (invitedUserEmail && invitedUserEmail.toLowerCase() === session.user.email.toLowerCase()) {
                        console.log('🔗 [ST Portal] Invitation detected, accepting invitation...');
                        try {
                            // Accepter l'invitation
                            const acceptResponse = await FamlaServices(
                                "api_host",
                                `/process/invitation/accept/${invitationToken}`,
                                "POST",
                                "",
                                token
                            );
                            
                            if (acceptResponse.status === 200) {
                                console.log('✅ [ST Portal] Invitation accepted successfully');
                                setInvitationError(null);
                                invitationProcessedRef.current = true;
                                setIsAuthenticating(false);
                                
                                // Rediriger vers le processus invité
                                navigate(`${Routes.PROCESS_INTERVIEW}${invitedProcessId}`, { replace: true });
                                
                                // Nettoyer sessionStorage après un court délai
                                setTimeout(() => {
                                    sessionStorage.removeItem('invitation_token');
                                    sessionStorage.removeItem('invited_user_email');
                                    sessionStorage.removeItem('invited_process_id');
                                }, 500);
                                return;
                            } else if (acceptResponse.status === 404) {
                                console.warn('⚠️ [ST Portal] Invitation token not found');
                                setInvitationError('Cette invitation n\'existe plus ou a été supprimée.');
                                sessionStorage.removeItem('invitation_token');
                                sessionStorage.removeItem('invited_user_email');
                                sessionStorage.removeItem('invited_process_id');
                            } else if (acceptResponse.status === 410) {
                                console.warn('⚠️ [ST Portal] Invitation token expired or already used');
                                const errorMsg = acceptResponse.body?.description || 'Cette invitation a expiré ou a déjà été utilisée.';
                                setInvitationError(errorMsg);
                                sessionStorage.removeItem('invitation_token');
                                sessionStorage.removeItem('invited_user_email');
                                sessionStorage.removeItem('invited_process_id');
                            } else if (acceptResponse.status === 403) {
                                console.warn('⚠️ [ST Portal] Invitation email mismatch');
                                setInvitationError('Cette invitation est destinée à une autre adresse email.');
                                sessionStorage.removeItem('invitation_token');
                                sessionStorage.removeItem('invited_user_email');
                                sessionStorage.removeItem('invited_process_id');
                            } else {
                                console.warn('⚠️ [ST Portal] Failed to accept invitation:', acceptResponse.status);
                                setInvitationError('Une erreur est survenue lors de l\'acceptation de l\'invitation.');
                            }
                        } catch (error) {
                            console.error('❌ [ST Portal] Error accepting invitation:', error);
                            setInvitationError('Une erreur est survenue lors de l\'acceptation de l\'invitation.');
                        }
                    } else {
                        console.warn('⚠️ [ST Portal] Invitation email mismatch:', {
                            invited: invitedUserEmail,
                            current: session.user.email
                        });
                        setInvitationError('Cette invitation est destinée à une autre adresse email.');
                        sessionStorage.removeItem('invitation_token');
                        sessionStorage.removeItem('invited_user_email');
                        sessionStorage.removeItem('invited_process_id');
                    }
                }
                
                // [FLUX NORMAL] Si pas d'invitation ou erreur
                setIsAuthenticating(false);

                const redirectParam =
                    sessionStorage.getItem('post_auth_redirect') ||
                    new URLSearchParams(window.location.search).get('redirect');
                if (redirectParam) {
                    sessionStorage.removeItem('post_auth_redirect');
                    navigate(redirectParam, { replace: true });
                    return;
                }

                if (session.company_id === null) {
                    console.log('🏢 [ST Portal] No company, redirecting to create company');
                    navigate(Routes.CREATE_MY_COMPANY);
                } else {
                    console.log('🏠 [ST Portal] Company exists, redirecting to home');
                    navigate(Routes.HOMEVIEW);
                }
            } else {
                console.log('❌ [ST Portal] Session load failed:', data.status);
                setIsAuthenticating(false);
            }
        } catch (e) {
            console.error('❌ [ST Portal] Error loading session:', e);
            setIsAuthenticating(false);
        }
    }, [dispatch, navigate]);

    useEffect(() => {
        // [INVITATION TOKEN] Vérifier si un token d'invitation est présent dans l'URL
        const urlParams = new URLSearchParams(window.location.search);
        const invitationToken = urlParams.get("invitation_token");
        
        if (invitationToken) {
            // Valider le token et stocker les informations dans sessionStorage
            validateInvitationToken(invitationToken).then((result: InvitationTokenResult) => {
                if (result.success && result.data) {
                    const data: InvitationTokenData = result.data;
                    sessionStorage.setItem('invited_process_id', data.process_id);
                    sessionStorage.setItem('invited_user_email', data.user_email);
                    sessionStorage.setItem('invitation_token', invitationToken);
                    setInvitationError(null);
                    console.log('🔗 [ST Portal] Invitation token validated, stored process_id and email in sessionStorage');
                } else {
                    console.warn('🔗 [ST Portal] Invalid or expired invitation token:', result.error);
                    setInvitationError(result.error || 'Cette invitation n\'est plus valide.');
                    // Nettoyer le token invalide
                    sessionStorage.removeItem('invitation_token');
                    sessionStorage.removeItem('invited_user_email');
                    sessionStorage.removeItem('invited_process_id');
                }
            }).catch((error: Error) => {
                console.error('🔗 [ST Portal] Error validating invitation token:', error);
                setInvitationError('Une erreur est survenue lors de la validation de l\'invitation.');
                sessionStorage.removeItem('invitation_token');
                sessionStorage.removeItem('invited_user_email');
                sessionStorage.removeItem('invited_process_id');
            });
        }
        
        // [EXTRACTION TOKENS URL] Vérifier si des tokens sont passés en URL params (callback SSO)
        const redirectParam = urlParams.get('redirect');
        if (redirectParam) {
            sessionStorage.setItem('post_auth_redirect', redirectParam);
        }

        const urlAccessToken = urlParams.get('access_token');
        const urlRefreshToken = urlParams.get('refresh_token');
        const urlIsFirstLogin = urlParams.get('is_first_login');
        const urlOauthError = urlParams.get('error');
        const urlOauthErrorDescription = urlParams.get('error_description');

        // [STOCKAGE TOKENS] Si tokens présents dans URL, les stocker dans cookies
        if (urlAccessToken && urlRefreshToken) {
            console.log('🔑 [ST Portal] Tokens reçus via URL, stockage dans les cookies...');
            
            // Stocker les tokens dans les cookies
            setAuthCookie('access_token', urlAccessToken);
            setAuthCookie('refresh_token', urlRefreshToken);
            setAuthCookie('logged_in', 'True');
            
            // [MARQUEUR ST] Marquer l'utilisateur comme venant du portail ST
            sessionStorage.setItem('st_portal_user', 'true');
            
            if (urlIsFirstLogin) {
                setAuthCookie('is_first_login', urlIsFirstLogin, { expires: 1 / 1440 }); // 1 minute
            }
            
            // Nettoyer l'URL pour enlever les tokens (sécurité)
            window.history.replaceState({}, document.title, '/st-portal');
            
            // Charger la session avec le token
            loadSession(urlAccessToken);
            return;
        }

        if (urlOauthError) {
            setOauthError(
                urlOauthErrorDescription
                || t('views.auth.stSsoErrorFallback', 'La connexion via STMicroelectronics a échoué. Veuillez réessayer.')
            );
            window.history.replaceState({}, document.title, '/st-portal');
        }
        
        // [AUTO-LOGIN] Vérifier si un cookie de session existe déjà
        const accessToken = Cookies.get('access_token');
        const loggedIn = Cookies.get('logged_in');
        const refreshToken = Cookies.get('refresh_token');
        const isFirstLogin = Cookies.get('is_first_login');
        
        // [DEBUG] Logs détaillés des cookies pour diagnostic
        console.log('🔍 [ST Portal] Checking authentication state:', {
            hasAccessToken: !!accessToken,
            loggedIn,
            refreshToken: !!refreshToken,
            isFirstLogin,
            timestamp: new Date().toISOString()
        });
        
        // [DEBUG] Tous les cookies disponibles
        console.log('🍪 [ST Portal] All available cookies:', document.cookie);
        
        // [DEBUG] Cookies individuels
        console.log('🍪 [ST Portal] Individual cookies:', {
            access_token: accessToken ? `${accessToken.substring(0, 20)}...` : 'NOT FOUND',
            logged_in: loggedIn || 'NOT FOUND',
            refresh_token: refreshToken ? `${refreshToken.substring(0, 20)}...` : 'NOT FOUND',
            is_first_login: isFirstLogin || 'NOT FOUND'
        });
        
        if (accessToken && loggedIn === 'True') {
            // [VÉRIFICATION INVITATION EN COURS] Ne pas charger la session si on vient de traiter une invitation
            if (invitationProcessedRef.current) {
                console.log('🔗 [ST Portal] Invitation being processed, skipping session load');
                return;
            }
            
            // [VÉRIFICATION SESSION DÉJÀ CHARGÉE] Éviter les appels multiples
            if (sessionLoadedRef.current) {
                console.log('🔑 [ST Portal] Session already loaded, skipping...');
                return;
            }
            
            console.log('🔑 Token trouvé dans les cookies (ST Portal), chargement de la session...');
            // [MARQUEUR ST] S'assurer que le marqueur ST est défini même pour les sessions existantes
            sessionStorage.setItem('st_portal_user', 'true');
            loadSession(accessToken);
        } else {
            console.log('❌ No valid authentication found, showing login button');
        }
    }, [loadSession, t]);

    const handleSTLogin = async () => {
        try {
            setIsLoading(true);
            // Redirection directe vers l'endpoint SSO ST
            const apiUrl = config.api.baseUrl;
            window.location.assign(`${apiUrl}/st/login`);
        } catch (error) {
            console.error('Erreur lors de la redirection SSO ST:', error);
            setIsLoading(false);
        }
    };

    // Afficher un loader pendant l'authentification automatique
    if (isAuthenticating) {
        return (
            <AuthLayout>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '300px',
                    gap: '20px'
                }}>
                    <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem', color: '#0B0B0C' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="fw-bold" style={{ color: '#0B0B0C' }}>{t('views.auth.authenticating') || 'Connexion en cours...'}</h5>
                    <p style={{ color: '#555', opacity: 0.9 }}>
                        {t('views.auth.pleaseWait') || 'Veuillez patienter...'}
                    </p>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <div className="w-50">
                {/* [HEADER] Titre identique à la page auth classique */}
                <h4 className="fw-bold" style={{ color: '#0B0B0C' }}>{t('views.auth.getStarted')}</h4>
                
                {/* [DESCRIPTION] Message personnalisé pour ST */}
                <p className="mt-3 mb-4" style={{ color: '#555' }}>
                    {t('views.auth.stWelcomeMessage', { 
                        appName: getTenantName(),
                        defaultValue: `Connectez-vous avec votre compte STMicroelectronics pour accéder à ${getTenantName()}`
                    })}
                </p>

                {/* [ERREUR INVITATION] Afficher les erreurs d'invitation */}
                {invitationError && (
                    <div className="alert alert-warning alert-dismissible fade show mt-3" role="alert">
                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                        <strong>Invitation invalide :</strong> {invitationError}
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={() => setInvitationError(null)}
                            aria-label="Close"
                        ></button>
                    </div>
                )}

                {oauthError && (
                    <div className="alert alert-danger alert-dismissible fade show mt-3" role="alert">
                        <i className="fa-solid fa-circle-exclamation me-2"></i>
                        <strong>{t('views.auth.stSsoErrorTitle', 'Échec de la connexion SSO ST')} :</strong> {oauthError}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setOauthError(null)}
                            aria-label="Close"
                        ></button>
                    </div>
                )}

                {/* [BOUTON SSO ST] Bouton principal d'authentification */}
                <Buttons
                    name={t('views.auth.continueWithST', { 
                        defaultValue: 'Continuer avec STMicroelectronics' 
                    })}
                    onClick={handleSTLogin}
                    isLoading={isLoading}
                    btnClass="btn-theme w-100 mt-4"
                    labelClass="text-white fw-bold"
                    styleProps={{ height: 45 }}
                    withIcon={true}
                    iconName="fa-solid fa-building"
                    arrowVisible={false}
                    arrowColor=''
                    spinnerColor='text-warning'
                />

                {/* [FOOTER] Informations légales */}
                <div className="mt-4 text-center">
                    <small style={{ color: '#888' }}>
                        <i className="fa-solid fa-shield-halved me-2"></i>
                        {t('views.auth.stSecurityMessage', {
                            defaultValue: 'Connexion sécurisée via le système d\'authentification STMicroelectronics'
                        })}
                    </small>
                </div>

                <div className='mt-mob'></div>
            </div>
        </AuthLayout>
    );
};

export default STDedicatedView;
